/**
 * Creates a fields API backed by the legacy wsClient.
 * This is the adapter for the existing cloud web-service.
 *
 * Cloud endpoint:
 *   GET  system/shop/:domain/customization/search/field
 *   POST system/shop/:domain/customization/search/field
 *
 * Legacy response shape (fields.list):
 *   { fields: [{id, title, weight, autocomplete}], attributes: { weight: [...] } }
 *
 * Save expects the canonical web-service POST body:
 *   { fields: [{id, weight, autocomplete}], domain }
 * After save the server returns 200 with no useful payload — we re-fetch.
 */
export function createLegacyFieldsApi() {
  let _wsClient = null;

  function getClient() {
    if (!_wsClient) {
      // eslint-disable-next-line global-require
      const { wsClient } = require('util/appUtils');
      _wsClient = wsClient;
    }
    return _wsClient;
  }

  return {
    /**
     * @param {string} shopId
     * @returns {Promise<{ fields: Array<{id, title, weight, autocomplete}> }>}
     */
    async load(shopId) {
      const response = await getClient().fetchFields(shopId, 'search');
      // response: { fields: [{id, title, weight, autocomplete, ...}], attributes }
      const fields = (response.fields || []).map((f) => ({
        id: f.id,
        title: f.title,
        weight: f.weight,
        autocomplete: Boolean(f.autocomplete),
      }));
      return { fields };
    },

    /**
     * @param {string} shopId
     * @param {Array<{id, title, weight, autocomplete}>} fields
     * @returns {Promise<{ fields: Array }>}
     */
    async save(shopId, fields) {
      const body = {
        fields: fields.map((field) => ({
          id: field.id,
          weight: field.weight,
          autocomplete: field.autocomplete,
        })),
        domain: shopId,
      };
      const result = await getClient().updateFields(shopId, 'search', body);
      if (!result || result.status !== 'ok') {
        const details = result && typeof result === 'object'
          ? Object.entries(result)
            .filter(([key]) => key !== 'status')
            .flatMap(([, value]) => value?.errors || [])
            .map(error => error.message)
            .filter(Boolean)
            .join('; ')
          : '';
        throw new Error(details || 'Field settings were rejected by the web-service');
      }
      // Re-fetch to get the authoritative post-save state
      return this.load(shopId);
    },
  };
}
